/**
 * @fileoverview
 * Contact Form Section for the homepage.
 *
 * This component renders:
 * - Section heading "Get in Touch"
 * - Contact form with Name, Email, Subject dropdown, Budget dropdown, Message textarea
 * - Turnstile widget for bot protection
 * - Form validation and submission handling with honeypot and timing detection
 *
 * Fulfills: VAL-HOME-023, VAL-HOME-024
 */

"use client";

import { useState, useRef, useCallback } from "react";
import { Trans, useLingui } from "@lingui/react/macro";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TurnstileWidget, type TurnstileWidgetHandle } from "@/components/TurnstileWidget";

import { User, Mail, MessageSquare, Check, Loader2 } from "lucide-react";
import { SectionShell, SectionContainer } from "../primitives";

type FormState = {
  name: string;
  email: string;
  subject: string;
  budget: string;
  message: string;
  // Honeypot fields (hidden from real users, bots fill them)
  _hp_website: string;
  _hp_phone: string;
  isSubmitting: boolean;
  isSuccess: boolean;
  isError: boolean;
  errorMessage: string;
};

const subjectOptions = [
  { value: "general", label: "General Inquiry" },
  { value: "sales", label: "Sales / Pricing" },
  { value: "support", label: "Technical Support" },
  { value: "partnership", label: "Partnership" },
  { value: "other", label: "Other" },
];

const budgetOptions = [
  { value: "under-1k", label: "Under $1,000" },
  { value: "1k-5k", label: "$1,000 - $5,000" },
  { value: "5k-10k", label: "$5,000 - $10,000" },
  { value: "10k-25k", label: "$10,000 - $25,000" },
  { value: "25k+", label: "$25,000+" },
  { value: "enterprise", label: "Enterprise (Custom)" },
];

/**
 * ContactFormSection renders the contact form with Turnstile protection.
 *
 * @returns JSX.Element
 */
export function ContactFormSection() {
  const { t } = useLingui();
  const formStartTimeRef = useRef<number>(Date.now());
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);
  const [turnstileToken, setTurnstileToken] = useState<string>("");

  const [formState, setFormState] = useState<FormState>({
    name: "",
    email: "",
    subject: "",
    budget: "",
    message: "",
    _hp_website: "",
    _hp_phone: "",
    isSubmitting: false,
    isSuccess: false,
    isError: false,
    errorMessage: "",
  });

  const handleTurnstileToken = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileError = useCallback(() => {
    setFormState((prev) => ({
      ...prev,
      isError: true,
      errorMessage: "Verification failed. Please try again.",
    }));
  }, []);

  const validateForm = (): boolean => {
    if (!formState.name.trim()) {
      setFormState((prev) => ({
        ...prev,
        isError: true,
        errorMessage: "Please enter your name.",
      }));
      return false;
    }

    if (!formState.email.trim()) {
      setFormState((prev) => ({
        ...prev,
        isError: true,
        errorMessage: "Please enter your email address.",
      }));
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formState.email)) {
      setFormState((prev) => ({
        ...prev,
        isError: true,
        errorMessage: "Please enter a valid email address.",
      }));
      return false;
    }

    if (!formState.message.trim()) {
      setFormState((prev) => ({
        ...prev,
        isError: true,
        errorMessage: "Please enter a message.",
      }));
      return false;
    }

    // Turnstile validation - only if site key is configured
    if (process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && !turnstileToken) {
      setFormState((prev) => ({
        ...prev,
        isError: true,
        errorMessage: "Please complete the verification.",
      }));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Reset error state
    setFormState((prev) => ({ ...prev, isError: false, errorMessage: "" }));

    // Validate form
    if (!validateForm()) {
      return;
    }

    setFormState((prev) => ({ ...prev, isSubmitting: true }));

    // Calculate timing for bot detection
    const formTiming = Date.now() - formStartTimeRef.current;

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formState.name,
          email: formState.email,
          subject: formState.subject || "General Inquiry",
          budget: formState.budget || undefined,
          message: formState.message,
          // Bot detection fields
          _hp_website: formState._hp_website,
          _hp_phone: formState._hp_phone,
          _timing: formTiming,
          // Turnstile token
          turnstileToken,
        }),
      });

      const data = (await response.json()) as { success?: boolean; error?: string };

      if (response.ok && data.success) {
        setFormState((prev) => ({
          ...prev,
          isSubmitting: false,
          isSuccess: true,
          name: "",
          email: "",
          subject: "",
          budget: "",
          message: "",
        }));
        // Reset Turnstile
        turnstileRef.current?.reset();
        setTurnstileToken("");
        // Reset form start time for potential re-submission
        formStartTimeRef.current = Date.now();
      } else {
        throw new Error(data.error ?? "Form submission failed");
      }
    } catch (error) {
      setFormState((prev) => ({
        ...prev,
        isSubmitting: false,
        isError: true,
        errorMessage:
          error instanceof Error
            ? error.message
            : "An error occurred. Please try again later.",
      }));
    }
  };

  return (
    <SectionShell
      id="contact"
      data-testid="contact-form-section"
      ariaLabel="Contact form section"
      noPadding
      className="py-10 sm:py-16 lg:py-20"
    >
      <SectionContainer>
        <div className="mx-auto max-w-3xl">
          {/* Section Title */}
          <div className="mb-12 text-center">
            {/* Subtitle */}
            <div className="mb-4 flex items-center justify-center gap-3">
              <div className="h-px w-8 bg-gray-400" />
              <span className="text-sm font-medium uppercase tracking-wider text-gray-600">
                <Trans>Contact Us</Trans>
              </span>
              <div className="h-px w-8 bg-gray-400" />
            </div>

            {/* Main Title */}
            <h2 className="text-2xl font-bold leading-tight text-gray-900 sm:text-3xl md:text-4xl">
              <Trans>Get in Touch</Trans>
            </h2>
            <p className="mt-4 text-gray-600">
              <Trans>Have a question or project in mind? We&apos;d love to hear from you.</Trans>
            </p>
          </div>

          {/* Form Card */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8 lg:p-10">
            {!formState.isSuccess ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot fields - hidden from real users */}
                <div className="hidden" aria-hidden="true">
                  <input
                    type="text"
                    name="_hp_website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formState._hp_website}
                    onChange={(e) =>
                      setFormState((prev) => ({ ...prev, _hp_website: e.target.value }))
                    }
                  />
                  <input
                    type="text"
                    name="_hp_phone"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formState._hp_phone}
                    onChange={(e) =>
                      setFormState((prev) => ({ ...prev, _hp_phone: e.target.value }))
                    }
                  />
                </div>

                {/* Name Input */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-900">
                    <Trans>Name</Trans> <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                    <Input
                      type="text"
                      id="name"
                      name="name"
                      placeholder={t`Your name`}
                      required
                      aria-required="true"
                      value={formState.name}
                      onChange={(e) =>
                        setFormState((prev) => ({ ...prev, name: e.target.value }))
                      }
                      disabled={formState.isSubmitting}
                      className="h-12 border-gray-200 pl-12"
                    />
                  </div>
                </div>

                {/* Email Input */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-900">
                    <Trans>Email</Trans> <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      placeholder={t`your@email.com`}
                      required
                      aria-required="true"
                      value={formState.email}
                      onChange={(e) =>
                        setFormState((prev) => ({ ...prev, email: e.target.value }))
                      }
                      disabled={formState.isSubmitting}
                      className="h-12 border-gray-200 pl-12"
                    />
                  </div>
                </div>

                {/* Subject Dropdown */}
                <div className="space-y-2">
                  <Label htmlFor="subject" className="text-gray-900">
                    <Trans>Subject</Trans>
                  </Label>
                  <Select
                    value={formState.subject}
                    onValueChange={(value) =>
                      setFormState((prev) => ({ ...prev, subject: value }))
                    }
                    disabled={formState.isSubmitting}
                  >
                    <SelectTrigger className="h-12 border-gray-200">
                      <SelectValue placeholder={t`Select a subject`} />
                    </SelectTrigger>
                    <SelectContent>
                      {subjectOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Budget Dropdown */}
                <div className="space-y-2">
                  <Label htmlFor="budget" className="text-gray-900">
                    <Trans>Budget Range</Trans>
                  </Label>
                  <Select
                    value={formState.budget}
                    onValueChange={(value) =>
                      setFormState((prev) => ({ ...prev, budget: value }))
                    }
                    disabled={formState.isSubmitting}
                  >
                    <SelectTrigger className="h-12 border-gray-200">
                      <SelectValue placeholder={t`Select your budget range`} />
                    </SelectTrigger>
                    <SelectContent>
                      {budgetOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Message Textarea */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-gray-900">
                    <Trans>Message</Trans> <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <Textarea
                      id="message"
                      name="message"
                      placeholder={t`Tell us about your project or question...`}
                      required
                      aria-required="true"
                      rows={5}
                      value={formState.message}
                      onChange={(e) =>
                        setFormState((prev) => ({ ...prev, message: e.target.value }))
                      }
                      disabled={formState.isSubmitting}
                      className="min-h-[140px] border-gray-200 pl-12 pt-2"
                    />
                  </div>
                </div>

                {/* Turnstile Widget */}
                <div className="pt-2">
                  <TurnstileWidget
                    ref={turnstileRef}
                    onToken={handleTurnstileToken}
                    onError={handleTurnstileError}
                    action="contact_form"
                    theme="auto"
                    size="normal"
                    className="flex justify-center"
                  />
                </div>

                {/* Error Message */}
                {formState.isError && (
                  <div
                    role="alert"
                    className="rounded-md bg-red-50 p-3 text-sm text-red-600"
                  >
                    {formState.errorMessage}
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={formState.isSubmitting}
                  className="h-12 w-full bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
                >
                  {formState.isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      <Trans>Sending...</Trans>
                    </>
                  ) : (
                    <Trans>Send Message</Trans>
                  )}
                </Button>
              </form>
            ) : (
              /* Success Message */
              <div
                role="alert"
                className="rounded-lg border border-green-200 bg-green-50 p-8 text-center animate-in fade-in duration-500"
              >
                <div className="mb-4 flex justify-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500/20">
                    <Check className="h-8 w-8 text-green-600" />
                  </div>
                </div>
                <h3 className="mb-2 text-xl font-semibold text-green-800">
                  <Trans>Thank you!</Trans>
                </h3>
                <p className="text-green-700">
                  <Trans>Your message has been sent successfully. We&apos;ll get back to you soon.</Trans>
                </p>
              </div>
            )}
          </div>
        </div>
      </SectionContainer>
    </SectionShell>
  );
}
